<template class="page-content">
  <ModalLayout :ref="modalRef" :modal-id="modalRef"
    :loading="isSavableStateLoading"
    @close="modalClosed">
    <template #modal-header>
      <h5 class="modal-title">
        Scheduled Notifications
      </h5>
    </template>
    <template #modal-body>
      <div class="row">
        <div class="col-sm-12 mb-2">
          <PortalTable ref="scheduled_notifications_table"
            table-id="scheduled_notifications_table"
            :table-data="userStore.scheduledNotifications"
            empty="No notifications found!"
            :total-records="userStore.scheduledNotifications.length"
            :loading="isSavableStateLoading"
            mode="local"
            :filter-values="filterValues"
            filter-display="row"
            @on-filter-change="onFilterChange"
            selection-mode="single">
            <template #columns>
              <Column field="unet_email.donor_code" 
                header="Donor"
                sortable
                sort-field="unet_email.donor_code"
                filter-field="unet_email.donor_code"
                :show-filter-menu="false">
                <template #filter="{ filterModel = { value: null }, filterCallback }">
                  <InputText v-model="filterModel.value"
                    type="text"
                    class="p-column-filter form-control"
                    placeholder="Search"
                    @input="filterCallback()" />
                </template>
              </Column>
               <Column field="unet_email.match_code" 
                header="Match"
                sortable
                sort-field="unet_email.match_code"
                filter-field="unet_email.match_code"
                :show-filter-menu="false">
                <template #filter="{ filterModel = { value: null }, filterCallback }">
                  <InputText v-model="filterModel.value"
                    type="text"
                    class="p-column-filter form-control"
                    placeholder="Search"
                    @input="filterCallback()" />
                </template>
              </Column>
              <Column field="unet_email.transplant_center_code" 
                sort-field="unet_email.transplant_center_code"
                header="TxC" 
                sortable
                filter-field="unet_email.transplant_center_code"
                :show-filter-menu="false"
                :filter-menu-style="{ width: '15rem' }">
                <template #filter>
                  <MultiSelectFilter ref="tc_id" 
                    filter-field="unet_email.transplant_center_code"
                    class="form-control form-control-select"
                    option-label="code"
                    option-value="code"
                    append-to="self"
                    :options="txcStore.transplantCenters"
                    @change="onChange" />
                </template>
              </Column>
              <Column field="unet_email.organ_group.name" 
                header="Organ" 
                filter-field="unet_email.organ_group.name"
                :show-filter-menu="false"
                :filter-menu-style="{ width: '15rem' }">
                <template #filter>
                  <MultiSelectFilter ref="organ_group_id" 
                    filter-field="unet_email.organ_group.name"
                    class="form-control form-control-select"
                    option-value="name"
                    option-label="name"
                    append-to="self"
                    :options="organGroupsStore.organGroups"
                    @change="onChange" />
                </template>
              </Column>
              <Column field="unet_email.opo_code" 
                header="OPO" 
                filter-field="unet_email.opo_code"
                :show-filter-menu="false">
                <template #filter="{ filterModel = { value: null }, filterCallback }">
                  <InputText v-model="filterModel.value"
                    type="text"
                    class="p-column-filter form-control"
                    placeholder="Search"
                    @input="filterCallback()" />
                </template>
              </Column>
              <Column field="sent_at"
                header="Notification Time" 
                sortable
                sort-field="sent_at"
                style="min-width: 100px;">
                <template #body="slotProps">
                  {{ formatDate(slotProps.data.sent_at) }}
                </template>
              </Column>
              <Column field="type" header="Type" style="min-width: 100px;">
                <template #body="slotProps">
                  {{  slotProps.data.unet_email.expedited ? 'Expedited ' : '' }}{{ to_snake_case(slotProps.data.unet_email.email_type) }}
                </template>
              </Column>               
              <Column header="Action" style="min-width: 100px;">
                <template #body="slotProps">
                  <ActionToolbar v-if="slotProps.data.sending_status == 'scheduled'"
                    :component-state="currentState" 
                    @primaryClick="dismissNotification(slotProps.data.id)"
                    primary-button-text="Dismiss"
                    primary-style="danger"
                    :secondary-enabled="false"
                    :error-message="formError">
                  </ActionToolbar>
                </template>
              </Column>
            </template>
          </PortalTable>
        </div>
      </div>
    </template>
  </ModalLayout>
</template>

<script lang="js">
import {APIRoute, EP} from "~/src/endpoints";
import beApiClient from "@/src/be-api-client";
import ModalLayout from "@/src/components/shared/ModalLayout.vue";
import ActionToolbar from "@/src/components/shared/ActionToolbar.vue";
import {savableStateMixin} from "@/src/mixins/savableStateMixin";
import {modalActionsMixin} from "@/src/mixins/modalActionsMixin";
import PortalTable from "@/src/components/shared/PortalTable.vue";
import Column from 'primevue/column';
import {useAppCoreStore} from "@/src/stores/app-core";
import { useUserStore } from '@/src/stores/user';
import { FilterMatchMode } from '@primevue/core/api';
import { useTxcStore } from '@/src/stores/transplant_centers';
import { useOrganGroupStore } from '@/src/stores/organ-groups';
import MultiSelectFilter from "@/src/components/shared/FormComponents/MultiSelectFilter.vue";
import InputText from 'primevue/inputtext';
import { formatDateTimeTotoLocaleString } from "@/src/date-helpers";

export default {
  components: {
    PortalTable,
    ModalLayout,
    Column,
    ActionToolbar,
    MultiSelectFilter,
    InputText
  },
  mixins: [savableStateMixin, modalActionsMixin],
  emits: ['close'],
  data() {
    return {
      scheduledNotifications: [],
      modalRef: 'scheduled-notifications-modal',
      errorMessage: null,
      filterValues: {
        "unet_email.donor_code" : { value: null, matchMode: FilterMatchMode.STARTS_WITH },
        "unet_email.match_code" : { value: null, matchMode: FilterMatchMode.STARTS_WITH },
        "unet_email.transplant_center_code" : { value: null, matchMode: FilterMatchMode.IN },
        "unet_email.opo_code" : { value: null, matchMode: FilterMatchMode.STARTS_WITH },
        "unet_email.organ_group.name" : { value: null, matchMode: FilterMatchMode.IN }
      },
      formatDate: formatDateTimeTotoLocaleString
    }
  },
  computed: {
    scheduledCount() {
      return this.userStore.scheduledNotificationsCount.count;
    }
  },
  watch: {
    'scheduledCount': {
      handler: async function() {
        await this.userStore.loadScheduledNotifications();
      },
      deep: true,
      immediate: true
    }
  },
  async mounted() {
    await this.getModalData();
  },
  setup() {
    const appStore = useAppCoreStore();
    const userStore = useUserStore();
    const txcStore = useTxcStore();
    const organGroupsStore = useOrganGroupStore();
    return {
      appStore, userStore, txcStore, organGroupsStore
    }
  },
  methods: {
    async dismissNotification(id) {
      const endpoint = APIRoute(EP.alternative_notifications.dismiss, { id: id });

      try {
        this.setSavableStateLoading();
        await beApiClient.put(endpoint);
        await this.getModalData()
      } catch(error) {
        this.handleFormError(error);
      }
    },
    to_snake_case(snakeCaseString) {
      return snakeCaseString[0].toUpperCase() + snakeCaseString.replace('_', ' ').slice(1)
    },
    async getModalData() {
      try {
        this.setSavableStateLoading();
        await this.organGroupsStore.loadAllOrganGroups();
        await this.txcStore.loadTransplantCentres();
        await this.userStore.loadScheduledNotificationsCount();

        this.setSavableStateIdle();
      } catch(error) {
        this.handleFormError(error);
      }
    },
    modalClosed() {
      this.setSavableStateBlank();
      this.$emit('close');
    },
    onChange(event) {
      this.$refs.scheduled_notifications_table.resetFilters(event);
    },
    onFilterChange(event) {
      if(event["unet_email.transplant_center_code"] === "") this.$refs.tc_id.clearFilters();
      if(event["unet_email.organ_name"] === "") this.$refs.organ_group_id.clearFilters();
    },
  }
}
</script>
